import React, { useMemo } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Provider, useSelector } from 'react-redux';
import { ThemeProvider, CssBaseline } from '@mui/material';
import { store } from './redux/store';
import { lightTheme, darkTheme } from './theme/theme';
import MainLayout from './layouts/MainLayout';
import ProtectedRoute from './components/ProtectedRoute';
import Login from './pages/Login';
import OTPVerification from './pages/OTPVerification';
import Dashboard from './pages/Dashboard';
import TVManagement from './pages/TVManagement';
import TVDetails from './pages/TVDetails';
import MediaLibrary from './pages/MediaLibrary';
import MenuBuilder from './pages/MenuBuilder';
import Settings from './pages/Settings';
import CanvasEditor from './canvas/CanvasEditor';
import CanvasEditorV2 from './canvas/CanvasEditorV2';

const AppContent = () => {
  const { theme } = useSelector((state) => state.user);
  const { isAuthenticated, takeawayList } = useSelector((state) => state.auth);
  // User is authenticated if they have a takeawayList (even after reload)
  const isUserAuthenticated = isAuthenticated || (takeawayList && takeawayList.length > 0);
  const currentTheme = useMemo(() => (theme === 'dark' ? darkTheme : lightTheme), [theme]);

  return (
    <ThemeProvider theme={currentTheme}>
      <CssBaseline />
      <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <Routes>
          {/* Public Routes */}
          <Route
            path="/login"
            element={isUserAuthenticated ? <Navigate to="/" replace /> : <Login />}
          />
          <Route
            path="/otp"
            element={isUserAuthenticated ? <Navigate to="/" replace /> : <OTPVerification />}
          />

          {/* Protected Routes */}
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <Dashboard />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/tvs"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <TVManagement />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/tvs/:id"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <TVDetails />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/media"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <MediaLibrary />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/menus"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <MenuBuilder />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <Settings />
                </MainLayout>
              </ProtectedRoute>
            }
          />

          {/* Canvas Editors */}
          <Route
            path="/canvas-editor"
            element={
              <ProtectedRoute>
                <CanvasEditorV2 />
              </ProtectedRoute>
            }
          />
          <Route
            path="/canvas-editor-v1"
            element={
              <ProtectedRoute>
                <CanvasEditor />
              </ProtectedRoute>
            }
          />

          {/* Catch all - redirect to login or dashboard */}
          <Route
            path="*"
            element={<Navigate to={isUserAuthenticated ? "/" : "/login"} replace />}
          />
        </Routes>
      </Router>
    </ThemeProvider>
  );
};

function App() {
  return (
    <Provider store={store}>
      <AppContent />
    </Provider>
  );
}

export default App;

